import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { AdminHttpService } from './admin-http.service';
import { Observable, map } from 'rxjs';
import { IAdminOrgOption } from '@interfaces/index';
import { OrgOptionLookup } from '@ui-components/admin/org-picker';

// Re-exported so existing importers of IAdminOrgOption from this service are
// unaffected by the interface moving to the shared library.
export { IAdminOrgOption };

export interface IAdminOrg {
  id: number;
  name: string | null;
  org_type_id: number;
  org_type_name: string;
  parent_org_id: number | null;
  parent_org_name: string | null;
  active: boolean;
}

export interface IAdminOrgListResult {
  orgs: IAdminOrg[];
  total: number;
  page: number;
  per_page: number;
}

export interface IAdminOrgType {
  id: number;
  name: string;
}

export interface IAdminOrgCreated {
  id: number;
  name: string;
  org_type_id: number;
  parent_org_id: number | null;
}

export interface IAdminOrgCreateInput {
  name: string;
  org_type_id: number;
  parent_org_id?: number;
}

export interface IAdminOrgUpdateInput {
  org_id: number;
  name: string;
  org_type_id: number;
  parent_org_id: number | null;
}

export interface IAdminOrgUpdated {
  id: number;
  name: string;
  org_type_id: number;
  parent_org_id: number | null;
}

export interface IAdminOrgDetail {
  id: number;
  name: string | null;
  org_type_id: number;
  org_type_name: string;
  parent_org_id: number | null;
  parent_org_name: string | null;
  active: boolean;
  created_at: string;
}

export interface IAdminOrgActionResult {
  id: number;
  active: boolean;
}

export interface IAdminOrgPerson {
  org_person_id: number;
  person_id: number;
  first_name: string;
  last_name: string;
  email_address: string;
  role_id: number;
  role_name: string;
  active: boolean;
}

export interface IAdminOrgPeopleResult {
  people: IAdminOrgPerson[];
  total: number;
  page: number;
  per_page: number;
}

export interface IAdminOrgChild {
  id: number;
  name: string;
  people_count: number;
  active: boolean;
}

export interface IAdminOrgChildrenResult {
  children: IAdminOrgChild[];
  total: number;
  page: number;
  per_page: number;
}

export interface IAdminOrgInventoryRow {
  assessment_group_id: number;
  assessment_group_name: string;
  allotted: number;
  used: number;
  available: number;
  purchased?: number;
  handed_out?: number;
}

export interface IAdminOrgInventoryResult {
  items: IAdminOrgInventoryRow[];
  shows_hierarchy_totals: boolean;
}

export interface IAdminOrgReceivingOrg {
  id: number;
  name: string;
}

export interface IAdminOrgAllocationContext {
  assessment_group_id: number;
  assessment_group_name: string;
  free_to_hand_out: number;
  receiving_orgs: IAdminOrgReceivingOrg[];
}

export interface IAdminOrgAllocationInput {
  viewed_org_id: number;
  org_id: number;
  assessment_group_id: number;
  amount: number;
}

export interface IAdminOrgAllocationResult {
  allocation_id: number;
  purchasing_org_id: number;
  receiving_org_id: number;
  assessment_id: number;
  amount: number;
}

@Injectable({
  providedIn: 'root',
})
export class AdminOrganisationsService implements OrgOptionLookup {

  constructor(protected _http_service: AdminHttpService) {}

  getOrgs(
    page: number,
    perPage: number,
    search?: string,
    status?: string,
    orgTypeId?: number | null
  ): Observable<IAdminOrgListResult> {
    let params = new HttpParams()
      .set('page', page)
      .set('per_page', perPage);
    const trimmedSearch = search?.trim();
    if (trimmedSearch) {
      params = params.set('search', trimmedSearch);
    }
    if (status) {
      params = params.set('status', status);
    }
    if (orgTypeId) {
      params = params.set('org_type_id', orgTypeId);
    }
    return this._http_service
      .get<IAdminOrgListResult>('admin/orgs', params)
      .pipe(map((response) => response.data));
  }

  createOrg(input: IAdminOrgCreateInput): Observable<IAdminOrgCreated> {
    return this._http_service
      .post<IAdminOrgCreated>('admin/orgs', input)
      .pipe(map((response) => response.data));
  }

  getOrgTypes(): Observable<IAdminOrgType[]> {
    return this._http_service
      .get<IAdminOrgType[]>('admin/orgs/types')
      .pipe(map((response) => response.data));
  }

  getOrgOptions(
    exclude_org_id?: number,
    search?: string,
    active_only?: boolean
  ): Observable<IAdminOrgOption[]> {
    let params = new HttpParams();
    if (exclude_org_id) {
      params = params.set('exclude_org_id', exclude_org_id);
    }
    const trimmedSearch = search?.trim();
    if (trimmedSearch) {
      params = params.set('search', trimmedSearch);
    }
    if (active_only) {
      params = params.set('active_only', active_only);
    }
    return this._http_service
      .get<IAdminOrgOption[]>('admin/orgs/options', params)
      .pipe(map((response) => response.data));
  }

  updateOrg(input: IAdminOrgUpdateInput): Observable<IAdminOrgUpdated> {
    return this._http_service
      .put<IAdminOrgUpdated>('admin/orgs', input)
      .pipe(map((response) => response.data));
  }

  getOrgDetail(org_id: number): Observable<IAdminOrgDetail> {
    const params = new HttpParams().set('org_id', org_id);
    return this._http_service
      .get<IAdminOrgDetail>('admin/orgs/detail', params)
      .pipe(map((response) => response.data));
  }

  deactivateOrg(org_id: number): Observable<IAdminOrgActionResult> {
    return this._http_service
      .post<IAdminOrgActionResult>('admin/orgs/deactivation', { org_id })
      .pipe(map((response) => response.data));
  }

  reactivateOrg(org_id: number): Observable<IAdminOrgActionResult> {
    return this._http_service
      .post<IAdminOrgActionResult>('admin/orgs/reactivation', { org_id })
      .pipe(map((response) => response.data));
  }

  getOrgPeople(org_id: number, page: number, per_page: number): Observable<IAdminOrgPeopleResult> {
    const params = new HttpParams()
      .set('org_id', org_id)
      .set('page', page)
      .set('per_page', per_page);
    return this._http_service
      .get<IAdminOrgPeopleResult>('admin/orgs/people', params)
      .pipe(map((response) => response.data));
  }

  getOrgChildren(org_id: number, page: number, per_page: number): Observable<IAdminOrgChildrenResult> {
    const params = new HttpParams()
      .set('org_id', org_id)
      .set('page', page)
      .set('per_page', per_page);
    return this._http_service
      .get<IAdminOrgChildrenResult>('admin/orgs/children', params)
      .pipe(map((response) => response.data));
  }

  getOrgInventory(org_id: number): Observable<IAdminOrgInventoryResult> {
    const params = new HttpParams().set('org_id', org_id);
    return this._http_service
      .get<IAdminOrgInventoryResult>('admin/orgs/inventory', params)
      .pipe(map((response) => response.data));
  }

  getAllocationContext(
    org_id: number,
    assessment_group_id: number
  ): Observable<IAdminOrgAllocationContext> {
    const params = new HttpParams()
      .set('org_id', org_id)
      .set('assessment_group_id', assessment_group_id);
    return this._http_service
      .get<IAdminOrgAllocationContext>('admin/orgs/inventory/allocation-context', params)
      .pipe(map((response) => response.data));
  }

  allocateInventory(input: IAdminOrgAllocationInput): Observable<IAdminOrgAllocationResult> {
    return this._http_service
      .post<IAdminOrgAllocationResult>('admin/orgs/inventory/allocations', input)
      .pipe(map((response) => response.data));
  }
}
